<script setup>
import moment from 'moment-timezone';
import { defineProps, onMounted, ref, watch, computed } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { MODAL_MODES } from 'dashboard/routes/dashboard/draftMessages/components/constants';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();
const { updateUISettings } = useUISettings();
const signatureBreakLine = '<br>-- <br>';

const showDetailIds = ref([]);

const scheduledAtText = (scheduledAt, timezone) => {
  if (!scheduledAt) return '';
  if (!timezone) return '';

  return moment(scheduledAt)
    .tz(timezone || Intl.DateTimeFormat().resolvedOptions().timeZone)
    .format('MMM DD, HH:mm A');
};

const onEditModal = draftId => {
  updateUISettings({
    editing_draft_id: draftId,
    draft_compose_mode: MODAL_MODES.EDITING,
  });
};

const onToggleDetail = draftId => {
  if (showDetailIds.value.includes(draftId)) {
    showDetailIds.value = showDetailIds.value.filter(id => id !== draftId);
  } else {
    showDetailIds.value.push(draftId);
  }
};

const loadRelatedDrafts = () => {
  store.dispatch('drafts/inConversation', {
    displayId: props.conversationId,
  });
};

const onCancelSend = async draftId => {
  await store.dispatch('drafts/cancelScheduled', draftId);
  loadRelatedDrafts();
  useAlert(t('DRAFTS.CANCEL_SEND_SUCCESS'));
};

const drafts = computed(() => {
  return store.getters['drafts/getDraftsByConversation'](props.conversationId);
});

onMounted(async () => {
  loadRelatedDrafts();
});

watch(
  () => props.conversationId,
  async () => {
    loadRelatedDrafts();
  }
);
</script>

<template>
  <ul class="list-none">
    <li
      v-for="draft in drafts"
      :key="draft.id"
      :class="showDetailIds.includes(draft.id) ? 'show-full' : ''"
    >
      <div
        class="flex flex-col bg-n-slate-3 p-3 mb-2 rounded-md border-2 border-violet-300 dark:border-violet-700 relative overflow-hidden"
      >
        <section
          v-if="draft.meta"
          class="space-y-1 rtl:pl-9 ltr:pr-9 text-sm break-words text-n-slate-11 pb-2 mb-3 border-b border-violet-200 dark:border-violet-800"
        >
          <div v-if="draft.meta.subject" class="text-n-slate-12 flex">
            <span class="me-2 w-16 text-end">
              {{ $t('EMAIL_HEADER.SUBJECT') }}
            </span>
            <span class="font-light">
              {{ draft.meta.subject }}
            </span>
          </div>
          <div v-if="draft.meta.cc_emails.length" class="text-n-slate-12 flex">
            <span class="me-2 w-16 text-end">
              {{ $t('EMAIL_HEADER.CC') }}
            </span>
            <span class="font-light">
              {{ draft.meta.cc_emails.join(', ') }}
            </span>
          </div>
          <div v-if="draft.meta.bcc_emails.length" class="text-n-slate-12">
            <span class="me-2 w-32 text-end">
              {{ $t('EMAIL_HEADER.BCC') }}
            </span>
            <span class="font-light">
              {{ draft.meta.bcc_emails.join(', ') }}
            </span>
          </div>
        </section>
        <section v-if="draft.meta" class="email-content">
          <div class="email-preview" v-html="draft.content" />
          <p
            v-if="draft.meta.use_signature"
            class="mt-2"
            v-html="signatureBreakLine"
          />
          <div
            v-if="draft.meta.use_signature"
            class="mt-2 pt-2"
            v-html="draft.inbox.signature"
          />
        </section>
        <section
          class="flex flex-row border-t mt-3 pt-3 text-n-slate-11 border-violet-200 dark:border-violet-800 justify-between items-center gap-2 relative z-10"
        >
          <div v-if="draft.scheduled_at">
            <NextButton
              v-tooltip.top="draft.timezone"
              sm
              ghost
              icon="i-lucide-calendar-clock"
              :label="scheduledAtText(draft.scheduled_at, draft.timezone)"
              @click.stop="onEditModal(draft.id)"
            />
          </div>
          <div v-else>
            <NextButton
              sm
              ghost
              icon="i-lucide-pencil"
              :label="$t('DRAFTS.EDIT_BUTTON')"
              @click.stop="onEditModal(draft.id)"
            />
          </div>
          <div v-if="draft.scheduled_at">
            <NextButton
              sm
              ruby
              confirmation
              :label="$t('DRAFTS.CANCEL_SEND_BUTTON')"
              @click.stop="onCancelSend(draft.id)"
            />
          </div>
        </section>
        <button
          v-if="!showDetailIds.includes(draft.id)"
          class="cursor-pointer bg-gradient-to-t from-n-slate-4 via-n-slate-4 via-20% to-transparent absolute z-1 left-0 right-0 bottom-0 w-full h-full flex items-end justify-center pb-16 rounded-none"
          @click="onToggleDetail(draft.id)"
        >
          <Icon icon="i-lucide-chevron-down" class="text-2xl text-n-slate-11" />
        </button>
      </div>
    </li>
  </ul>
</template>

<style lang="scss">
.email-preview {
  font-family: Arial, Helvetica, sans-serif;
  font-size: 14px;
  line-height: 1.4;
  margin: 0;
  padding: 0;

  p {
    margin: 16px 0;
  }

  h1 {
    font-size: 24px;
    font-weight: bold;
    margin: 16px 0;
  }

  h2 {
    font-size: 20px;
    font-weight: bold;
    margin: 16px 0;
  }

  h3 {
    font-size: 16px;
    font-weight: bold;
    margin: 16px 0;
  }

  a {
    color: #1a0dab;
    text-decoration: underline;
  }

  ul,
  ol {
    margin: 16px 0;
    padding-left: 40px;
  }
  ol {
    list-style-type: decimal;
  }

  img {
    max-width: 100%;
    height: auto;
    display: inline-block;
    vertical-align: baseline;
    border: none;
  }

  table {
    border-collapse: collapse;
    border-spacing: 0;

    td {
      vertical-align: top;
    }
  }
}

.email-content {
  @apply h-32 overflow-hidden;
}

.show-full .email-content {
  height: initial;
}
</style>
